/**
 * Complete FileManager Component
 * A macOS Finder-style file manager with virtualization, drag-drop, and full feature set
 *
 * Features:
 * - Two-pane layout (folder tree + main view)
 * - Grid and list view modes
 * - Multi-select with keyboard shortcuts
 * - Drag & drop for moving files
 * - Context menus
 * - Upload with progress tracking
 * - Breadcrumb navigation
 * - Optimistic updates with rollback
 */

import React, { useState, useRef, useMemo } from "react";
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import { FolderPlus, Upload, Grid3x3, List, Folder } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useFileManager } from "./provider";
import { FileNode } from "./types";
import { Breadcrumbs } from "./Breadcrumbs";
import { ContextMenu, getContextMenuItems } from "./ContextMenu";
import {
  RenameModal,
  NewFolderModal,
  DeleteConfirmModal,
  ChangeFolderPermissionModal,
} from "./modals";
import { FileCard, FileRow, getFileIcon } from "./FileRenderers";
import { FolderTree } from "./FolderTree";
import { useSelection } from "./hooks/useSelection";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts";
import { useSelectionBox } from "./hooks/useSelectionBox";
import { useToast } from "../../contexts/ToastContext";
import {
  CreateShareLinkModal,
  CreateShareLinkParams,
} from "../../components/ShareLinks/CreateShareLinkModal";
import { apiClient } from "../../utils/apiClient/index.ts";
import { useAuth } from "../../contexts/AuthContext";
import { useOrg } from "../../contexts/OrgContext";
import { apiUpdateFolderPermission } from "./api";

export interface FileManagerProps {
  className?: string;
  showFolderTree?: boolean;
}

export function FileManager({
  className = "",
  showFolderTree = true,
}: FileManagerProps) {
  const { currentUser } = useAuth();
  const { navigateOrg, orgId } = useOrg();
  const navigate = useNavigate();
  const {
    state,
    performAction,
    deselectAll,
    setActiveFolder,
    getNodePath,
    getChildrenOfFolder,
    showContextMenu,
    hideContextMenu,
  } = useFileManager();
  const { showToast } = useToast();

  // Get user's role in current organization
  const userRole = currentUser?.organizations.find(
    (org) => org.id === orgId,
  )?.role;

  const [draggedNodes, setDraggedNodes] = useState<FileNode[]>([]);
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [loadedFolders, setLoadedFolders] = useState<Set<string>>(new Set());

  // Load folder contents when active folder changes
  React.useEffect(() => {
    // Don't lazy load for root (null) - root is loaded by DocumentsTabNew
    if (!state.activeFolderId) {
      return;
    }

    const folderId = state.activeFolderId;

    // Skip if we've already loaded this folder
    if (loadedFolders.has(folderId)) {
      return;
    }

    // Load folder contents
    performAction({ type: "listFolder", nodeId: folderId })
      .then(() => {
        setLoadedFolders((prev) => new Set(prev).add(folderId));
      })
      .catch((error) => {
        console.error("Failed to load folder contents:", error);
      });
  }, [state.activeFolderId, loadedFolders, performAction]);

  // Custom collision detection that works well with sidebar
  const customCollisionDetection = (args: any) => {
    // First try pointerWithin for precise detection
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
    // Fall back to closestCenter for better sidebar detection
    return closestCenter(args);
  };

  // Modal state
  const [renameModal, setRenameModal] = useState<{
    isOpen: boolean;
    nodeId: string;
    currentName: string;
  }>({ isOpen: false, nodeId: "", currentName: "" });

  const [newFolderModal, setNewFolderModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [shareModal, setShareModal] = useState<{
    isOpen: boolean;
    nodeId: string;
  }>({ isOpen: false, nodeId: "" });
  const [permissionModal, setPermissionModal] = useState<{
    isOpen: boolean;
    nodeId: string;
  }>({ isOpen: false, nodeId: "" });

  // Refs for selection box
  const mainPaneRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Get current folder's children
  const currentChildren = useMemo(() => {
    const children = getChildrenOfFolder(state.activeFolderId);

    // Sort children
    const sorted = [...children].sort((a, b) => {
      const { key, dir } = state.sortBy;
      let compareResult = 0;

      if (key === "name") {
        compareResult = a.name.localeCompare(b.name);
      } else if (key === "updatedAt") {
        compareResult =
          new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
      } else if (key === "size") {
        compareResult = (a.size || 0) - (b.size || 0);
      }

      // Folders first, then apply sort
      if (a.type === "folder" && b.type !== "folder") return -1;
      if (a.type !== "folder" && b.type === "folder") return 1;

      return dir === "asc" ? compareResult : -compareResult;
    });

    return sorted;
  }, [state.activeFolderId, state.sortBy, getChildrenOfFolder]);

  // Selection
  const selectedNodes = useMemo(() => {
    return Array.from(state.selectedIds)
      .map((id) => state.nodes[id])
      .filter(Boolean);
  }, [state.selectedIds, state.nodes]);

  const selection = useSelection({
    selectedIds: state.selectedIds,
    onSelect: (id) => performAction({ type: "select", nodeId: id }),
    onDeselect: (id) => performAction({ type: "deselect", nodeId: id }),
    onSetSelected: (ids) =>
      performAction({ type: "multiSelect", nodeIds: ids }),
    onClearSelection: deselectAll,
    allVisibleIds: currentChildren.map((n) => n.id),
  });

  // Selection box
  const selectionBox = useSelectionBox({
    containerRef: mainPaneRef,
    itemRefs: itemRefs.current,
    onSelectionChange: (ids) =>
      performAction({ type: "multiSelect", nodeIds: ids }),
    enabled: state.viewMode === "list", // Enable only in list view for simplicity
  });

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onSelectAll: selection.selectAll,
    // Note: onDelete intentionally removed - users must use context menu to prevent accidental deletion
    onRename: () => {
      if (state.selectedIds.size === 1) {
        const nodeId = Array.from(state.selectedIds)[0];
        const node = state.nodes[nodeId];
        if (node) {
          setRenameModal({ isOpen: true, nodeId, currentName: node.name });
        }
      }
    },
    onEscape: () => {
      if (state.contextMenu) {
        hideContextMenu();
      } else if (state.selectedIds.size > 0) {
        deselectAll();
      }
    },
  });

  // Drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum drag distance
      },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const draggedId = active.id as string;

    // If dragging a selected item, drag all selected items
    const itemsToDrag = state.selectedIds.has(draggedId)
      ? selectedNodes
      : [state.nodes[draggedId]].filter(Boolean);

    setDraggedNodes(itemsToDrag);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { over } = event;

    // Always clear dragged nodes at the end
    setDraggedNodes([]);

    // If no valid drop target, don't perform move
    if (!over || !draggedNodes.length) {
      return;
    }

    const targetId = over.id as string;

    // Handle dropping on root
    if (targetId === "root") {
      const draggedNode = draggedNodes[0];
      // Don't move if already at root
      if (draggedNode && draggedNode.parentId === null) {
        return;
      }

      // Move all dragged nodes to root
      performAction({
        type: "move",
        nodeIds: draggedNodes.map((n) => n.id),
        targetParentId: null,
      }).catch(console.error);
      return;
    }

    const targetNode = state.nodes[targetId];

    // Can only drop onto folders
    if (!targetNode || targetNode.type !== "folder") {
      return;
    }

    // Don't move if dropping on the same parent
    const draggedNode = draggedNodes[0];
    if (draggedNode && draggedNode.parentId === targetId) {
      return;
    }

    // Move all dragged nodes
    performAction({
      type: "move",
      nodeIds: draggedNodes.map((n) => n.id),
      targetParentId: targetId,
    }).catch(console.error);
  };

  // Handlers
  const handleItemClick = (node: FileNode, event: React.MouseEvent) => {
    const newLastSelected = selection.handleItemClick(
      node.id,
      event,
      lastSelectedId || undefined,
    );
    setLastSelectedId(newLastSelected);
  };

  const handleItemDoubleClick = (node: FileNode) => {
    if (node.type === "folder") {
      setActiveFolder(node.id);
    } else if (node.type === "form_submission") {
      // Navigate to shared route (accessible by both admin and client)
      navigate(`/form-submissions/${node.id}`);
    }
  };

  const handleContextMenu = (
    node: FileNode | null,
    event: React.MouseEvent,
  ) => {
    event.preventDefault();

    // If right-clicking non-selected item, select it
    if (node && !state.selectedIds.has(node.id)) {
      performAction({ type: "multiSelect", nodeIds: [node.id] });
    }

    showContextMenu(event.clientX, event.clientY, node?.id);
  };

  const handleUpload = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    performAction({
      type: "upload",
      files: Array.from(files),
      targetParentId: state.activeFolderId || undefined,
    }).catch((error) => {
      const errorMessage =
        error instanceof Error ? error.message : "Upload failed";
      showToast(`Upload failed: ${errorMessage}`, "error");
    });
  };

  // Native file drag and drop handlers
  const handleFileDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if dragging files from file system
    if (e.dataTransfer?.types?.includes("Files")) {
      setIsDraggingFiles(true);
    }
  };

  const handleFileDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Show copy cursor for file drops
    if (e.dataTransfer?.types?.includes("Files")) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const handleFileDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Only hide if leaving the main pane entirely
    if (e.currentTarget === e.target) {
      setIsDraggingFiles(false);
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDraggingFiles(false);

    // Only handle file drops from file system
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  };

  // Breadcrumb path
  const breadcrumbPath = state.activeFolderId
    ? getNodePath(state.activeFolderId)
    : [];

  // Share handler
  const handleCreateShareLink = async (params: CreateShareLinkParams) => {
    const node = state.nodes[shareModal.nodeId];
    if (!node || node.type !== "file") {
      return { publicUrl: "", id: "" };
    }

    const result = await apiClient.createShareLink({
      resourceType: "file",
      resourceId: node.id,
      ...params,
    });

    return {
      publicUrl: result.publicUrl,
      id: result.id,
    };
  };

  // Get current folder's permission (for clients)
  const currentFolder = state.activeFolderId
    ? state.nodes[state.activeFolderId]
    : null;
  const currentFolderPermission = currentFolder?.clientPermission || "none";

  // Determine permission for context menu
  // If a single folder is selected, use that folder's permission
  // Otherwise, use the current active folder's permission
  const contextPermission =
    selectedNodes.length === 1 && selectedNodes[0].type === "folder"
      ? selectedNodes[0].clientPermission || "none"
      : currentFolderPermission;

  // Context menu items
  const contextMenuItems = getContextMenuItems({
    selectedNodes,
    userRole, // From current organization
    currentFolderPermission: contextPermission,
    onViewDetails: () => {
      if (selectedNodes.length === 1) {
        const node = selectedNodes[0];
        if (node.type === "file") {
          // Org-specific route (admin or client)
          navigateOrg(`/files/${node.id}`);
        } else if (node.type === "form_submission") {
          // Org Route for viewing details of form submission (not submission itself)
          navigateOrg(`/form-submissions/${node.id}`);
        }
      }
    },
    onRename: () => {
      if (selectedNodes.length === 1) {
        setRenameModal({
          isOpen: true,
          nodeId: selectedNodes[0].id,
          currentName: selectedNodes[0].name,
        });
      }
    },
    onDelete: () => setDeleteModal(true),
    onDownload: () => {
      selectedNodes.forEach((node) => {
        if (node.type === "file") {
          performAction({ type: "download", nodeId: node.id }).catch(
            console.error,
          );
        }
      });
    },
    onShare: () => {
      if (
        selectedNodes.length === 1 &&
        (selectedNodes[0].type === "file" ||
          selectedNodes[0].type === "form_submission")
      ) {
        setShareModal({
          isOpen: true,
          nodeId: selectedNodes[0].id,
        });
      }
    },
    onNewFolder: () => setNewFolderModal(true),
    onChangePermission: () => {
      if (selectedNodes.length === 1 && selectedNodes[0].type === "folder") {
        setPermissionModal({
          isOpen: true,
          nodeId: selectedNodes[0].id,
        });
      }
    },
  });

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className={`flex flex-col h-full ${className}`}>
        {/* Toolbar */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <Breadcrumbs
            path={breadcrumbPath}
            onNavigate={(node) => setActiveFolder(node?.id || null)}
          />

          <div className="flex items-center gap-2">
            {/* View mode toggle */}
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              <button
                onClick={() =>
                  performAction({ type: "setViewMode", viewMode: "grid" })
                }
                className={`p-1.5 rounded dark:text-white ${
                  state.viewMode === "grid"
                    ? "bg-white dark:bg-gray-700 shadow-sm"
                    : "hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
                title="Grid view"
              >
                <Grid3x3 className="w-4 h-4" />
              </button>
              <button
                onClick={() =>
                  performAction({ type: "setViewMode", viewMode: "list" })
                }
                className={`p-1.5 rounded dark:text-white ${
                  state.viewMode === "list"
                    ? "bg-white dark:bg-gray-700 shadow-sm"
                    : "hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
                title="List view"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Actions */}
            <button
              onClick={() => setNewFolderModal(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <FolderPlus className="w-4 h-4" />
              New Folder
            </button>

            <label className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 rounded-lg transition-colors cursor-pointer">
              <Upload className="w-4 h-4" />
              Upload
              <input
                type="file"
                multiple
                onChange={(e) => handleUpload(e.target.files)}
                className="hidden"
              />
            </label>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Folder tree sidebar */}
          {showFolderTree && (
            <FolderTree
              nodes={state.nodes}
              activeFolderId={state.activeFolderId}
              onNavigate={setActiveFolder}
              className="w-64 flex-shrink-0"
            />
          )}

          {/* Main pane */}
          <div
            ref={mainPaneRef}
            className="flex-1 overflow-auto p-4 relative"
            onMouseDown={selectionBox.handleMouseDown}
            onContextMenu={(e) => handleContextMenu(null, e)}
            onDragEnter={handleFileDragEnter}
            onDragOver={handleFileDragOver}
            onDragLeave={handleFileDragLeave}
            onDrop={handleFileDrop}
          >
            {state.loading ? (
              <div className="flex items-center justify-center h-64">
                <p className="text-gray-500">Loading...</p>
              </div>
            ) : currentChildren.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <Folder className="w-16 h-16 mb-4 text-gray-300" />
                <p>This folder is empty</p>
                <p className="text-sm mt-2">Drop files here or click Upload</p>
              </div>
            ) : state.viewMode === "grid" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {currentChildren.map((node) => (
                  <FileCard
                    key={node.id}
                    ref={(el) => {
                      if (el) itemRefs.current.set(node.id, el);
                      else itemRefs.current.delete(node.id);
                    }}
                    node={node}
                    selected={state.selectedIds.has(node.id)}
                    onClick={(e) => handleItemClick(node, e)}
                    onDoubleClick={() => handleItemDoubleClick(node)}
                    onContextMenu={(e) => handleContextMenu(node, e)}
                    uploadProgress={state.uploads[node.id]?.progress}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                {currentChildren.map((node) => (
                  <FileRow
                    key={node.id}
                    ref={(el) => {
                      if (el) itemRefs.current.set(node.id, el);
                      else itemRefs.current.delete(node.id);
                    }}
                    node={node}
                    selected={state.selectedIds.has(node.id)}
                    onClick={(e) => handleItemClick(node, e)}
                    onDoubleClick={() => handleItemDoubleClick(node)}
                    onContextMenu={(e) => handleContextMenu(node, e)}
                    uploadProgress={state.uploads[node.id]?.progress}
                  />
                ))}
              </div>
            )}

            {/* Selection box overlay */}
            {selectionBox.selectionBox.visible && (
              <div
                className="fixed border-2 border-blue-500 bg-blue-500/10 pointer-events-none"
                style={{
                  left: Math.min(
                    selectionBox.selectionBox.startX,
                    selectionBox.selectionBox.endX,
                  ),
                  top: Math.min(
                    selectionBox.selectionBox.startY,
                    selectionBox.selectionBox.endY,
                  ),
                  width: Math.abs(
                    selectionBox.selectionBox.endX -
                      selectionBox.selectionBox.startX,
                  ),
                  height: Math.abs(
                    selectionBox.selectionBox.endY -
                      selectionBox.selectionBox.startY,
                  ),
                }}
              />
            )}

            {/* File drop zone overlay */}
            {isDraggingFiles && (
              <div className="absolute inset-0 bg-blue-500/10 border-4 border-dashed border-blue-500 pointer-events-none flex items-center justify-center z-50">
                <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-8 border-2 border-blue-500">
                  <Upload className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                  <p className="text-xl font-semibold text-gray-900 dark:text-gray-100 text-center">
                    Drop files here to upload
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center mt-2">
                    {state.activeFolderId
                      ? `Files will be uploaded to current folder`
                      : `Files will be uploaded to root`}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Context menu */}
      {state.contextMenu && (
        <ContextMenu
          x={state.contextMenu.x}
          y={state.contextMenu.y}
          items={contextMenuItems}
          onClose={hideContextMenu}
        />
      )}

      {/* Modals */}
      <RenameModal
        isOpen={renameModal.isOpen}
        onClose={() =>
          setRenameModal({ isOpen: false, nodeId: "", currentName: "" })
        }
        currentName={renameModal.currentName}
        itemType={
          state.nodes[renameModal.nodeId]?.type === "folder" ? "folder" : "file"
        }
        onRename={async (newName) => {
          await performAction({
            type: "rename",
            nodeId: renameModal.nodeId,
            newName,
          });
        }}
      />

      <NewFolderModal
        isOpen={newFolderModal}
        onClose={() => setNewFolderModal(false)}
        userRole={userRole}
        onCreateFolder={async (name, clientPermission) => {
          await performAction({
            type: "createFolder",
            folderName: name,
            clientPermission,
            targetParentId: state.activeFolderId || undefined,
          });
        }}
      />

      <DeleteConfirmModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        itemCount={state.selectedIds.size}
        itemNames={selectedNodes.map((n) => n.name)}
        onDelete={async () => {
          await performAction({
            type: "delete",
            nodeIds: Array.from(state.selectedIds),
          });
        }}
      />

      <CreateShareLinkModal
        isOpen={shareModal.isOpen}
        onClose={() => setShareModal({ isOpen: false, nodeId: "" })}
        onCreate={handleCreateShareLink}
        resourceType="file"
      />

      <ChangeFolderPermissionModal
        isOpen={permissionModal.isOpen}
        onClose={() => setPermissionModal({ isOpen: false, nodeId: "" })}
        folderName={state.nodes[permissionModal.nodeId]?.name || ""}
        currentPermission={
          state.nodes[permissionModal.nodeId]?.clientPermission || "none"
        }
        onChangePermission={async (newPermission) => {
          const updatedNode = await apiUpdateFolderPermission(
            permissionModal.nodeId,
            newPermission,
          );
          console.log("parentNode", updatedNode.parentId);
          // Update the node in state
          performAction({
            type: "listFolder",
            nodeId: updatedNode.parentId || undefined,
          }).catch(console.error);
          showToast("Folder permission updated successfully", "success");
        }}
      />

      {/* Drag overlay */}
      <DragOverlay>
        {draggedNodes.length > 0 && (
          <div className="p-2 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 flex items-center gap-2">
            {getFileIcon(draggedNodes[0], "w-5 h-5")}
            <span className="text-sm font-medium">
              {draggedNodes.length === 1
                ? draggedNodes[0].name
                : `${draggedNodes.length} items`}
            </span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
